/**
 * Password Strength Indicator Component
 * ======================================
 * Visual password strength indicator with requirements checklist
 */

import React from 'react';

// Password requirements (matches backend validation)
export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
  requireSpecial: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?'
};

/**
 * Calculate password strength
 * @param {string} password - Password to evaluate
 * @returns {object} - Strength level and passed requirements
 */
export const calculatePasswordStrength = (password) => {
  if (!password) {
    return { strength: 0, level: 'none', passed: [] };
  }

  const passed = [];
  let score = 0;

  // Length check (12+ chars)
  if (password.length >= PASSWORD_REQUIREMENTS.minLength) {
    passed.push('length');
    score += 25;
  }

  // Uppercase letter
  if (/[A-Z]/.test(password)) {
    passed.push('uppercase');
    score += 20;
  }

  // Lowercase letter
  if (/[a-z]/.test(password)) {
    passed.push('lowercase');
    score += 20;
  }

  // Digit
  if (/\d/.test(password)) {
    passed.push('digit');
    score += 20;
  }

  // Special character
  const specialCharsRegex = new RegExp(`[${PASSWORD_REQUIREMENTS.specialChars.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}]`);
  if (specialCharsRegex.test(password)) {
    passed.push('special');
    score += 15;
  }

  // Determine strength level
  let level = 'weak';
  if (score >= 80) level = 'strong';
  else if (score >= 60) level = 'medium';

  return { strength: score, level, passed };
};

const PasswordStrengthIndicator = ({ password }) => {
  const { strength, level, passed } = calculatePasswordStrength(password);

  // Color schemes for different strength levels
  const strengthColors = {
    none: { bg: 'bg-gray-200', text: 'text-gray-600' },
    weak: { bg: 'bg-red-500', text: 'text-red-600' },
    medium: { bg: 'bg-yellow-500', text: 'text-yellow-600' },
    strong: { bg: 'bg-green-500', text: 'text-green-600' }
  };

  const colors = strengthColors[level];

  // Requirements list
  const requirements = [
    { key: 'length', label: `At least ${PASSWORD_REQUIREMENTS.minLength} characters` },
    { key: 'uppercase', label: 'One uppercase letter (A-Z)' },
    { key: 'lowercase', label: 'One lowercase letter (a-z)' },
    { key: 'digit', label: 'One number (0-9)' },
    { key: 'special', label: `One special character (${PASSWORD_REQUIREMENTS.specialChars})` }
  ];

  if (!password) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Strength Bar */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Password Strength</span>
          <span className={`text-sm font-medium capitalize ${colors.text}`}>
            {level === 'none' ? '' : level}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${colors.bg}`}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
        <ul className="space-y-2">
          {requirements.map(({ key, label }) => {
            const isPassed = passed.includes(key);
            return (
              <li key={key} className="flex items-start">
                <span className="flex-shrink-0 mt-0.5">
                  {isPassed ? (
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </span>
                <span className={`ml-2 text-sm ${isPassed ? 'text-gray-700' : 'text-gray-500'}`}>
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
